import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useT } from '@/i18n/LocaleProvider';
import {
  PINNED_SIDEBAR_DEFAULT_WIDTH,
  pinnedSidebarWidthBounds,
  clampPinnedSidebarWidth,
  pinnedSidebarWidthForKey,
  storedPinnedSidebarWidth,
  storePinnedSidebarWidth,
} from './pinnedSidebarWidth';

export {
  PINNED_SIDEBAR_DEFAULT_WIDTH,
  PINNED_SIDEBAR_MIN_WIDTH,
  PINNED_SIDEBAR_MAX_WIDTH,
  PINNED_SIDEBAR_MAX_VIEWPORT_RATIO,
  PINNED_SIDEBAR_KEYBOARD_STEP,
  PINNED_SIDEBAR_WIDTH_STORAGE_KEY,
  pinnedSidebarWidthBounds,
  clampPinnedSidebarWidth,
  pinnedSidebarWidthForKey,
  storedPinnedSidebarWidth,
} from './pinnedSidebarWidth';

function currentViewportWidth(): number {
  return typeof window === 'undefined' ? 1280 : window.innerWidth;
}

/**
 * F-0585: the pinned sidebar's `aside`, its width chosen by the handle on its right edge.
 *
 * As the Activity split does (I-0629), the person's choice and what is shown are kept apart: the
 * choice is only ever set by the person and is what gets stored; the width drawn is that choice
 * clamped to the window as it is now, so a window that narrows for a moment and widens back returns
 * to the choice (PSW-6).
 *
 * A drag never renders React (PSW-1): each pointer move schedules one frame, and that frame writes
 * the new width to the `aside`'s and the handle's own attributes. Only the drag's end commits it to
 * state. `children` is an element made by `Layout`, so that commit re-renders this wrapper and not
 * the sidebar inside it.
 *
 * `pinned` is `Layout`'s `lg` query: below it the `aside` is hidden and holds nothing, the handle
 * included (T-0559: a phone launch draws no sidebar it does not show).
 */
export function PinnedSidebar({ pinned, children }: { pinned: boolean; children: ReactNode }) {
  const t = useT();
  const [preferredWidth, setPreferredWidth] = useState(
    () => storedPinnedSidebarWidth() ?? PINNED_SIDEBAR_DEFAULT_WIDTH,
  );
  const [viewportWidth, setViewportWidth] = useState(currentViewportWidth);
  const [dragging, setDragging] = useState(false);
  const width = clampPinnedSidebarWidth(preferredWidth, viewportWidth);
  const bounds = pinnedSidebarWidthBounds(viewportWidth);
  const asideRef = useRef<HTMLElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);
  const choiceMadeRef = useRef(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    width: number;
    frame: number;
  } | null>(null);

  useEffect(() => {
    const followViewport = () => setViewportWidth(currentViewportWidth());
    window.addEventListener('resize', followViewport);
    return () => window.removeEventListener('resize', followViewport);
  }, []);

  useEffect(() => {
    // Only a choice the person made is written; a restored or default width is not one.
    if (choiceMadeRef.current) storePinnedSidebarWidth(preferredWidth);
  }, [preferredWidth]);

  useEffect(
    () => () => {
      if (dragRef.current) cancelAnimationFrame(dragRef.current.frame);
    },
    [],
  );

  function choose(nextWidth: number): void {
    choiceMadeRef.current = true;
    setPreferredWidth(clampPinnedSidebarWidth(nextWidth, viewportWidth));
  }

  function startResize(event: PointerEvent<HTMLSpanElement>): void {
    // The main button or a touch only; a right-click opens no drag.
    if (event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: width,
      width,
      frame: 0,
    };
    setDragging(true);
  }

  function moveResize(event: PointerEvent<HTMLSpanElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.width = clampPinnedSidebarWidth(
      drag.startWidth + event.clientX - drag.startX,
      currentViewportWidth(),
    );
    if (drag.frame) return;
    drag.frame = requestAnimationFrame(() => {
      drag.frame = 0;
      asideRef.current?.style.setProperty('width', `${drag.width}px`);
      handleRef.current?.setAttribute('aria-valuenow', String(drag.width));
    });
  }

  function stopResize(event: PointerEvent<HTMLSpanElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    cancelAnimationFrame(drag.frame);
    dragRef.current = null;
    setDragging(false);
    if (drag.width !== drag.startWidth) choose(drag.width);
    else asideRef.current?.style.setProperty('width', `${width}px`);
  }

  function resizeWithKeyboard(event: KeyboardEvent<HTMLSpanElement>): void {
    const next = pinnedSidebarWidthForKey(event.key, event.shiftKey, width, viewportWidth);
    if (next === null) return;
    event.preventDefault();
    choose(next);
  }

  return (
    <aside
      ref={asideRef}
      data-pinned-sidebar=""
      className="relative hidden h-full shrink-0 bg-[#F1F2F2] dark:bg-[#171717] lg:flex"
      style={{ width }}
    >
      {children}
      {pinned ? (
        /*
          PSW-9: on a coarse pointer a finger needs 44 px, drawn by a pseudo-element whose press is
          the handle's: 10 px in over the sidebar, whose lines end 12 px from its edge, and 34 px
          out over the page, which starts its controls 36 px out on a coarse pointer (PSW-10,
          `Layout`'s page column). Being no element, it is not read as part of the sidebar drawn
          past its edge (`ipad-sidebar-layout.spec.ts`). A `span`, so the sidebar's one `div` child
          is still its column (`coarseTargets.ts`, `sidebarParts`).
          F-0590: the 10 px in are pixels, as the lines' 12 px are, at every text size: in rem the
          handle reached 31 px in at the largest size and lay over Sign out's cell.
          F-0577: the line is a 2 px border drawn in tokens: the border colour on hover, the focus
          ring's on keyboard focus (named on the handle, the tab stop) and while dragging, so it
          follows both themes and Colour. I-1939: `[&:focus-visible>span]:`, the handle's focus;
          the old variant, focus-visible before the child selector, compiled to the line's own focus.
        */
        <span
          ref={handleRef}
          role="separator"
          aria-label={t('navigation.sidebar.resize')}
          aria-orientation="vertical"
          aria-valuemin={bounds.min}
          aria-valuemax={bounds.max}
          aria-valuenow={width}
          tabIndex={0}
          title={t('navigation.reorder.resizeHint')}
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={stopResize}
          onPointerCancel={stopResize}
          onDoubleClick={() => choose(PINNED_SIDEBAR_DEFAULT_WIDTH)}
          onKeyDown={resizeWithKeyboard}
          className="group absolute inset-y-0 right-0 z-20 flex w-[8px] cursor-col-resize touch-none select-none justify-end outline-none [&:focus-visible>span]:!border-[color:var(--evx-focus)] [@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:inset-y-0 [@media(pointer:coarse)]:before:-left-[2px] [@media(pointer:coarse)]:before:w-touch [@media(pointer:coarse)]:before:content-['']"
        >
          <span
            aria-hidden="true"
            className={`h-full w-0 border-l-2 border-transparent transition-colors duration-150 group-hover:border-[color:var(--evx-border)] ${
              dragging ? '!border-[color:var(--evx-focus)]' : ''
            }`}
          />
        </span>
      ) : null}
    </aside>
  );
}
